import * as React from 'react'
import IconButton from '@mui/material/IconButton'
import Icon from '@mdi/react'
import ToBottomIcon from '@mui/icons-material/ArrowDownward'
import ToTopIcon from '@mui/icons-material/ArrowUpward'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import CopyIcon from '@mui/icons-material/FileCopyOutlined'
import MoveIcon from '@mui/icons-material/DragIndicator'
import { FormattedMessage } from 'react-intl'
import Tooltip from '@mui/material/Tooltip'
import ConfirmDialog from 'components/ConfirmDialog'
import styles from './PlannedActionsMenu.pcss'
import { RecycleSelector } from './components/RecycleSelector'
import { RepeatIndicator } from './components/RepeatIndicator'
import Checkbox, { CheckboxProps } from '@mui/material/Checkbox'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ShareIcon from '@mui/icons-material/Send'
import { mdiArrowCollapseUp, mdiArrowCollapseDown } from '@mdi/js'

export interface PlannedActionsMenuProps {
  withRecycle: boolean
  selected: boolean
  recycle?: boolean | Date | number
  recycleReadonly?: boolean
  withRepeat?: boolean
  repeatLength?: number
  repeatPeriod?: string
  repeatUntil?: string
  className?: string
  btnClassName?: string
  withMoveControls: boolean | null
  dragHandleProps?: any
  virtual?: {
    repeatingLabel: string
    isForever: boolean
    type: 'repeat' | 'recycle'
  }
  checkboxProps?: CheckboxProps
  checkboxContainerClassName?: string
  menuBtnClassName?: string
  virtualLabelClassName?: string
  onToggleSelected: () => void
  onEdit: () => void
  onShareNow?: () => void
  onDelete?: () => void
  onCopy?: () => void
  onMoveToTop?: () => void
  onMoveToBottom?: () => void
  onMoveUp?: () => void
  onMoveDown?: () => void
  onRecycleChange?: (value: boolean | Date | number) => void
}

export const PlannedActionsMenu = React.memo((props: PlannedActionsMenuProps) => {
  const { virtual, recycle } = props
  const [anchorEl, setAnchorEl] = React.useState(null)

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget as any)
  }

  const closeMenu = () => {
    setAnchorEl(null)
  }

  const menuAction = (fn: () => void) => () => {
    closeMenu()
    fn()
  }

  const shareNow = () => {
    props.onShareNow?.()
    closeMenu()
  }

  const deletePost = () => {
    props.onDelete?.()
    closeMenu()
  }

  const stopPropagation = (e: any) => {
    e.stopPropagation()
  }

  const virtualPostLabel = React.useMemo(() => {
    if (!virtual) {
      return null
    }
    const labelArr = virtual.repeatingLabel.split(' ')
    return typeof recycle === 'number' || virtual.isForever
      ? labelArr.shift() + `\n${labelArr.join(' ')}`
      : virtual.repeatingLabel
  }, [virtual, recycle])

  const isVirtual = Boolean(virtual)

  return (
    <div className={`${styles['flex-row']} ${props.className || ''} ${virtual ? styles.virtual : ''}`}>
      {props.withMoveControls && (
        <React.Fragment>
          <div className={`${styles['flex-col']} ${styles.box} ${virtual?.type === 'recycle' ? styles.hidden : ''}`}>
            {props.onMoveUp && (
              <Tooltip title={<FormattedMessage id="post.actions.move-up" />} placement="top">
                <span>
                  <IconButton
                    key="move-up"
                    data-testid="btn-move-up"
                    size="small"
                    disabled={isVirtual}
                    className={`${styles['btn-action']} ${props.btnClassName || ''}`}
                    onClick={props.onMoveUp}
                  >
                    <ToTopIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {props.onMoveDown && (
              <Tooltip title={<FormattedMessage id="post.actions.move-down" />} placement="top">
                <span>
                  <IconButton
                    key="move-down"
                    data-testid="btn-move-down"
                    size="small"
                    disabled={isVirtual}
                    className={`${styles['btn-action']} ${props.btnClassName || ''}`}
                    onClick={props.onMoveDown}
                  >
                    <ToBottomIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
          <div className={`${styles['flex-col']} ${styles.box} ${virtual?.type === 'recycle' ? styles.hidden : ''}`}>
            <Tooltip title={<FormattedMessage id="post.actions.move-to-top" />} placement="top">
              <span>
                <IconButton
                  key="move-top"
                  data-testid="btn-move-top"
                  size="small"
                  disabled={isVirtual}
                  className={`${styles['btn-action']} ${props.btnClassName || ''}`}
                  onClick={props.onMoveToTop}
                >
                  <Icon size="20px" path={mdiArrowCollapseUp} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={<FormattedMessage id="post.actions.move-to-bottom" />} placement="top">
              <span>
                <IconButton
                  key="move-bottom"
                  data-testid="btn-move-bottom"
                  size="small"
                  disabled={isVirtual}
                  className={`${styles['btn-action']} ${props.btnClassName || ''}`}
                  onClick={props.onMoveToBottom}
                >
                  <Icon size="20px" path={mdiArrowCollapseDown} />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </React.Fragment>
      )}
      {props.withMoveControls && props.dragHandleProps && (
        <div className={styles.move} {...props.dragHandleProps}>
          <MoveIcon />
        </div>
      )}

      <div className={`${styles['flex-row']} ${styles.box} ${styles.mid}`}>
        {props.withRecycle && props.recycle !== undefined && props.onRecycleChange && (
          <RecycleSelector
            value={props.recycle}
            className={styles['btn-action']}
            activeClassName={styles.on}
            disabled={props.recycleReadonly}
            onChange={props.onRecycleChange}
          />
        )}
        {props.withRepeat && (
          <RepeatIndicator
            className={styles['btn-action']}
            repeatLength={props.repeatLength as number}
            repeatPeriod={props.repeatPeriod as string}
            repeatUntil={props.repeatUntil as string}
          />
        )}
        {virtual && (
          <div className={`${styles['recycle-label']} ${props.virtualLabelClassName || ''}`} data-testid="recycle-label">
            {virtualPostLabel}
          </div>
        )}
      </div>
      {!virtual && (
        <IconButton
          aria-label="More"
          aria-haspopup="true"
          size="small"
          className={props.menuBtnClassName || ''}
          data-testid="post-actions-menu-btn"
          onClick={openMenu}
        >
          <MoreVertIcon />
        </IconButton>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        classes={{ paper: styles.menu }}
        data-testid="post-actions-menu"
        onClose={closeMenu}
        onClick={stopPropagation}
      >
        <MenuItem onClick={menuAction(props.onEdit)}>
          <EditIcon className={styles.icon} />
          <FormattedMessage id="post.actions.edit" />
        </MenuItem>
        {props.onCopy && (
          <MenuItem onClick={menuAction(props.onCopy)}>
            <CopyIcon className={styles.icon} />
            <FormattedMessage id="post.actions.copy" />
          </MenuItem>
        )}
        {props.onShareNow && (
          <ConfirmDialog
            message={<FormattedMessage id="post.notifications.confirm-share-now" />}
            confirmLabel={<FormattedMessage id="actions.yes" />}
            data-testid="confirm-share-now"
          >
            {(confirm) => (
              <MenuItem onClick={confirm(shareNow)}>
                <ShareIcon className={styles.icon} />
                <FormattedMessage id="post.actions.share-now" />
              </MenuItem>
            )}
          </ConfirmDialog>
        )}
        {props.onDelete && (
          <ConfirmDialog
            message={<FormattedMessage id="post.delete-confirm" />}
            confirmLabel={<FormattedMessage id="actions.yes" />}
            variant="danger"
            data-testid="confirm-delete"
          >
            {(confirm) => (
              <MenuItem onClick={confirm(deletePost)}>
                <DeleteIcon className={styles.icon} />
                <FormattedMessage id="post.actions.delete" />
              </MenuItem>
            )}
          </ConfirmDialog>
        )}
      </Menu>
      {!virtual && (
        <div className={`${styles.box} ${props.checkboxContainerClassName || ''}`}>
          <Checkbox
            {...props.checkboxProps}
            color="primary"
            onClick={stopPropagation}
            checked={props.selected}
            onChange={props.onToggleSelected}
          />
        </div>
      )}
    </div>
  )
})

export default PlannedActionsMenu
